import { getInfiltrationScore } from "../hsa/infiltration"
import { IHealthySoilsAssessment, IHsaDrainage, IHsaDrillType, IHsaPrimaryCultivations } from "../hsa/types"
import { soilColors, components, defineComponent, FieldViewModel, getDateString, getMorganIndexTextFromValue, getSoilUnitOptions, IGenericAnalysis, IOmniaCommand, IOmniaCommandGroup, IOmniaSession, IOmniaShell, IOmniaShellDialogOptions, isArrayEmpty, labels, models, shell, soil, SoilAnalysisType, SoilUnit } from "../omnia"
import { IAnalysisEditorContext, IAnalysisValueColumn, IFieldsService, ISoilModuleContext, ISoilService } from "../types"
import { TableColumns } from "./columns"
import { RowManager, sortAnalysisRows } from "./rowManager"
import { RowViewModel } from "./rowViewModel"
import { TableView } from "./view"
import { HealthySoilsAssessmentViews } from "./views/hsa"
import { NitrogenViews } from "./views/nitrogen"
import { PcnViews } from "./views/pcn"
import { SoilViews } from "./views/soil"
import { SsmViews } from "./views/ssm"

const settingView = "view"
const settingSubView = "subview"
const settingColumns = "columnsvisibility"
const settingAnalysisColumns = "soilcolumnsvisibility"
const settingHsaColumns = "hsatablecolumnsvisibility"

export class SoilAnalysisTableCtrl implements IAnalysisEditorContext {

    public static create(): ng.IDirective {

        return defineComponent(SoilAnalysisTableCtrl, "app/soil/table/table.html", {
            context: "="
        })

    }

    public readonly readOnly = this.session.isReadOnly(p => p.soil)

    protected rowManager: RowManager

    public currentView: TableView
    public currentSubView: TableView
    public views: TableView[]

    private context: ISoilModuleContext

    private _componentPinned: boolean

    private search: string
    private allocationFilter = "0"

    public columns: TableColumns = new TableColumns()

    public showPlotReference: boolean

    private _rows: RowViewModel[]
    private _sortedRows: RowViewModel[]

    private _fieldCommands: IOmniaCommandGroup
    private _zoneCommands: IOmniaCommandGroup
    private _zoneFieldCommands: IOmniaCommandGroup
    private _analysisCommands: IOmniaCommandGroup
    private _analysisZonalCommands: IOmniaCommandGroup
    headerCommands: IOmniaCommandGroup
    private _hsaCommands: IOmniaCommandGroup
    private _hsaAllocCommands: IOmniaCommandGroup
    private _pcnCommands: IOmniaCommandGroup
    private _pcnAllocCommands: IOmniaCommandGroup

    private hsaDrainages: IHsaDrainage[]
    private primaryCultivations: IHsaPrimaryCultivations[]
    private drillTypes: IHsaDrillType[]


    static $inject = ["$scope", "$uibModal", "OmniaSession", "OmniaSoilService", "OmniaShell"]

    constructor(
        private $scope: ng.IScope,
        private $uibModal,
        private session: IOmniaSession,
        private svc: ISoilService,
        private shell: IOmniaShell,
        public readonly units = getSoilUnitOptions()) {
    }


    $onInit() {

        const { svc, $scope } = this

        this.rowManager = new RowManager(svc)

        var columnsVisibility = this.svc.settings.get(settingColumns)
        var analysisColumnsVisibility = this.svc.settings.get(settingAnalysisColumns)
        var hsaColumnsVisibility = this.svc.settings.get(settingHsaColumns)

        if (columnsVisibility != null) {
            this.columns.columns.forEach(c => c.visible = c.mandatory ? true : columnsVisibility[c.id])
        }

        if (analysisColumnsVisibility != null) {
            this.columns.standardAnalysisColumns.forEach(c => c.visible = analysisColumnsVisibility[c.key ? c.key : c.name])
        }

        if (hsaColumnsVisibility != null) {
            this.columns.hsaColumns.forEach(c => c.visible = hsaColumnsVisibility[c.key ? c.key : c.name])
        }

        this.hsaDrainages = this.svc.getHsaDrainage()
        this.primaryCultivations = this.svc.getPrimaryCultivations()
        this.drillTypes = this.svc.getDrillTypes()

        this.setupWatchers($scope)

    }

    private setup() {

        if (!this.context) {
            return
        }

        this.setupDocking()
        this.setupCommands()
        this.setupViews()

    }


    private setupWatchers($scope: ng.IScope) {

        $scope.$watchGroup(["vm.context", "vm.svc.stateVersion"], () => this.setup())

        $scope.$watchGroup(["vm.showZones", "vm.search", "vm.allocationFilter"], () => this._rows ? this.filterAndSortRows() : null)

        $scope.$watch("vm.serializedColumnsDescription", () => {
            var columns = []
            this.columns.columns.forEach(c => columns[c.id] = c.visible)
            this.svc.settings.set(settingColumns, columns)
        })

        $scope.$watch("vm.serializedSoilAnalysisColumnsDescription", () => {
            var columns = {}
            this.columns.standardAnalysisColumns.forEach(c => columns[c.key ? c.key : c.name] = c.visible)
            this.svc.settings.set(settingAnalysisColumns, columns)
        })

        $scope.$watch("vm.serializedHsaColumnsDescription", () => {
            var columns = {}
            this.columns.hsaColumns.forEach(c => columns[c.key ? c.key : c.name] = c.visible)
            this.svc.settings.set(settingHsaColumns, columns)
        })

    }

    private setupDocking() {

        this._componentPinned = false

        let ctxt = this.context.getCurrentContext()

        if (this.shell.mobile) {
            if (ctxt) ctxt.pin()
            this._componentPinned = true
        }

        this.$scope.$watch(
            () => {
                let ctxt = this.context.getCurrentContext()
                return ctxt && ctxt.isPinned
            },
            value => {
                this._componentPinned = value
            })

    }

    public refresh(analysisId: number, targetId?: number) {

        this.svc.refresh().then(() => {

            this.setupRows(this.currentSubView)

            let row = this.rows.find(r => (analysisId ? r.Id == analysisId : r.targetId == targetId))

            if ((!row) && this.rows.length) {
                row = this.rows[0]
            }

        })

    }

    private setupViews() {

        this.views = createViews(this.svc)

        let key = this.svc.settings.get(settingView, "groupsoil")
        let subKey = this.svc.settings.get(settingSubView, "soil")
        let view: TableView

        if (key) {
            view = this.views.find(v => v && (v.key === key))

            if (view) {
                this.currentView = view
                view = view.views.find(v => v && (v.key === subKey))
                this.selectSubView(view)
                return
            }
        }

        view = this.views[0]

        this.selectView(view)

    }

    public selectView(view: TableView) {

        this.currentView = view
        this.selectSubView(view.views[0])

        if (this.svc && view) {
            this.svc.settings.set(settingView, view.key)
        }

    }

    public selectSubView(view: TableView) {

        this.setCurrentView(view)

        if (this.svc && view) {
            this.svc.settings.set(settingSubView, view.key)
        }

    }


    private setCurrentView(view: TableView) {
        this.currentSubView = view
        this.setupRows(view)
    }

    private setupRows(view: TableView) {

        if (!view) {

            this._rows = null
            this.showPlotReference = false

        }
        else {

            let analyses = view.getAnalyses(this.svc)

            if (!analyses) {

                if (view.nitrogenAnalysis) {
                    analyses = this.svc.getNitrogenAnalysis()
                }
                else if (view.hsa) {
                    analyses = this.svc.getHealthySoilsAssessments()
                }
                else if (view.pcn) {
                    analyses = this.svc.getPcnAnalysis()
                }
                else {
                    analyses = this.svc.getSoilAnalyses(SoilAnalysisType.Standard)
                }

            }

            // 211105 - Usersnap 2328: do not display analyses allocated only to inactive fields
            // Note: the view.analysis property indicates that this view displays analyses, rather than allocations
            if (view.analysis) {
                const active = new Set(this.svc.activeFields.map(f => f.fieldId))
                analyses = (analyses || []).filter(a => isArrayEmpty(a.Allocations) || a.Allocations.some(a => active.has(a.FieldId)))
            }

            this._rows = analyses.map(a => new RowViewModel(a, this.svc))

            this.showPlotReference = view.standardAnalysis && this._rows.some(r => !!r.PlotReference)

        }

        this.filterAndSortRows()

    }

    get unit() {
        return this.svc.units.soil
    }

    set unit(value: SoilUnit) {
        this.svc.units.soil = value
    }

    getColumnUnit(c: IAnalysisValueColumn) {

        const { unit } = this

        if (c.index && (unit === SoilUnit.Index)) {
            return "index"
        }

        if (c.morgan && (unit === SoilUnit.Morgan)) {
            return "mg/l"
        }

        return c.units

    }

    getColumnValue(c: IAnalysisValueColumn, row: RowViewModel) {

        const { unit } = this

        type SupportedColumns = keyof typeof soil.conversions
        const key = (c.key ?? c.name) as SupportedColumns

        if (c.index && (unit === SoilUnit.Index)) {
            return this.getAnalysisIndex(c, row.getAnalysisValue(c), row)
        }

        const value = row.getAnalysisValue(c)
        const morgan = row.soilAnalysis?.P2O5Method === models.SoilP2O5Method.Morgan

        if (c.morgan && (unit === SoilUnit.Morgan)) {
            return morgan
                ? value
                : soil.conversions[key].toMorgan(value)
        }

        if (c.morgan && (unit === SoilUnit.Ppm)) {
            return morgan
                ? soil.conversions[key].toOlsen(value)
                : value
        }

        return row.getAnalysisValue(c)

    }

    get rows(): RowViewModel[] {
        return this._sortedRows
    }

    get serializedColumnsDescription(): string {
        return this.columns.columns.map(c => c.id + "-" + c.visible).join(";")
    }

    get serializedSoilAnalysisColumnsDescription(): string {
        return this.columns.standardAnalysisColumns.map(c => c.key + "-" + c.visible).join(";")
    }

    get serializedHsaColumnsDescription(): string {
        return this.columns.hsaColumns.map(c => c.key + "-" + c.visible).join(";")
    }

    get showToolbar() {
        return !(this.shell.mobile && this._componentPinned)
    }

    get fieldsView(): boolean {
        return this.currentSubView && this.currentSubView.fields
    }

    get zonesView(): boolean {
        return this.currentSubView && this.currentSubView.zones
    }

    get showAllocations(): boolean {
        return this.currentSubView && !this.currentSubView.fields
    }

    get nitrogenView(): boolean {
        return this.currentSubView?.nitrogenAnalysis
    }

    get hsaView(): boolean {
        return this.currentSubView?.hsa
    }

    get pcnView(): boolean {
        return this.currentSubView?.pcn
    }

    get soilView(): boolean {
        return this.currentSubView?.standardAnalysis
    }

    get analysisColumns(): IAnalysisValueColumn[] {

        const sv = this.currentSubView

        let columns = sv?.analysisColumns

        if ((!columns) && this.soilView) {
            return this.columns.standardAnalysisColumns
        }

        return columns

    }

    get hsaColumns(): IAnalysisValueColumn[] {
        return this.columns.hsaColumns
    }

    get analysisOrderInProgress(): boolean {
        return this.svc.getAnalysisOrderInProgress()
    }

    getCommands(row: RowViewModel): IOmniaCommandGroup {

        var fields = this.fieldsView
        var zones = this.zonesView

        if (zones) {
            if (row.soilAnalysisLayerId) {
                return this._zoneFieldCommands
            }
            return this._zoneCommands
        }

        if (fields && row.field) {
            if (this.nitrogenView) return null
            if (this.hsaView) return this._hsaAllocCommands
            if (this.pcnView) return this._pcnAllocCommands
            return this._fieldCommands
        }

        if (this.hsaView) {
            return this._hsaCommands
        }

        if (this.pcnView) {
            return this._pcnCommands
        }

        if (row.soilAnalysis && row.soilAnalysis.ZoneAnalysis) {
            return this._analysisZonalCommands
        }

        return this._analysisCommands

    }

    private filterAndSortRows() {

        var filteredRows = this._rows

        if (this.allocationFilter == "1") {
            // With
            filteredRows = filteredRows.filter(r => !r.unallocated)
        } else if (this.allocationFilter == "2") {
            // Without
            filteredRows = filteredRows.filter(r => r.unallocated)
        }

        if (this.search != null && this.search.length > 0) {

            let searchExp = new RegExp(this.search, "i")

            filteredRows = filteredRows.filter(r =>

                searchExp.test(r.AnalysisNumber)
                    || (r.Laboratory && searchExp.test(r.Laboratory.Name))
                    || (r.field && searchExp.test(r.field.getDescription()))
                    || (r.SamplingDate && searchExp.test(getDateString(r.SamplingDate)))
                    || (r.FieldReference && searchExp.test(r.FieldReference))
                    || (r.allocations && r.allocations.some(a => searchExp.test(a.Description)))


            )

        }

        if (this.fieldsView) {

            var rows = this.rowManager.groupRowsByField(filteredRows, !!this.nitrogenView)

            this._sortedRows = rows

        } else if (this.zonesView) {

            this.rowManager.groupRowsByZone(filteredRows, this._rows).then(r => this._sortedRows = r)

        }
        else {

            const flt = !this.nitrogenView && !this.pcnView

            this._sortedRows = sortAnalysisRows(filteredRows, flt)

        }

    }

    getGaugeStyle(c: IAnalysisValueColumn, value: number, row: RowViewModel) {

        const sa = row?.soilAnalysis

        if (c.morgan && (sa?.P2O5Method == models.SoilP2O5Method.Morgan)) {
            const key = c.key || c.name
            const clr = soilColors.getMorganColors(key as any)
            return soilColors.getGaugeStyle(value, clr)
        }
        else {
            return this.currentSubView.getGaugeStyle(c, value, row)
        }

    }

    getAnalysisIndex(c: IAnalysisValueColumn, value: number, row?: RowViewModel) {


        const sa = row?.soilAnalysis
        const key = c.key || c.name

        if (c.morgan && (sa?.P2O5Method == models.SoilP2O5Method.Morgan)) {
            return getMorganIndexTextFromValue(key as any, value)
        }
        else {
            return soilColors.getValueIndex(value, key)
        }

    }

    private setupCommands() {

        let editHsa = <IOmniaCommand>{
            text: "Edit healty soils assessment",
            icon: "fas fa-pen",
            foreground: true,
            execute: (r: RowViewModel) => this.editAnalysis(r.analysis)
        }

        let allocHsaCmd = <IOmniaCommand>{
            text: "Manage field allocations",
            icon: "alloc",
            foreground: true,
            execute: (r: RowViewModel) => this.manageFieldHsaAllocations(r.field),
            canExecute: () => !this.readOnly
        }

        this._hsaAllocCommands = new components.OmniaCommandGroup(
            "HSA Allocations",
            null,
            [allocHsaCmd])

        this._hsaCommands = new components.OmniaCommandGroup(
            "HSA",
            null,
            [editHsa])

        let createAnalysisCmd = <IOmniaCommand>{
            text: "Create",
            icon: "addnew",
            foreground: true,
            execute: () => this.createNewAnalysis(),
            canExecute: () => !this.readOnly
        }

        this.headerCommands = new components.OmniaCommandGroup(
            "Header",
            null,
            [createAnalysisCmd])

        let allocFieldCmd = <IOmniaCommand>{
            text: "Manage field allocations",
            icon: "alloc",
            foreground: true,
            execute: (r: RowViewModel) => this.manageFieldAllocations(r.field),
            canExecute: () => !this.readOnly
        }

        let editAnalysisCmd = <IOmniaCommand>{
            text: "Edit soil analysis",
            icon: "fas fa-pen",
            foreground: true,
            canExecute: (r: RowViewModel) => r?.hasAnalysis(),
            execute: (r: RowViewModel) => this.editAnalysis(r.analysis)
        }

        this._fieldCommands = new components.OmniaCommandGroup(
            "Field",
            null,
            [allocFieldCmd, editAnalysisCmd]
        )

        this._analysisCommands = new components.OmniaCommandGroup(
            "Soil analysis",
            null,
            [editAnalysisCmd]
        )

        this._analysisZonalCommands = new components.OmniaCommandGroup(
            "Zonal soil analysis",
            null,
            [editAnalysisCmd]
        )

        this._zoneFieldCommands = new components.OmniaCommandGroup(
            "Zone",
            null,
            [editAnalysisCmd, {
                text: "Generate average soil analysis",
                icon: "calc",
                foreground: true,
                canExecute: r => this.canGenerateSoilAnalysis(r),
                execute: r => this.generateAverageSoilAnalysis(r)
            }]
        )

        this._zoneCommands = new components.OmniaCommandGroup(
            "Zone",
            null,
            [editAnalysisCmd]
        )

        let allocPcnCmd = <IOmniaCommand>{
            text: "Manage field allocations",
            icon: "alloc",
            foreground: true,
            execute: (r: RowViewModel) => this.manageFieldPcnAllocations(r.field),
            canExecute: () => !this.readOnly
        }

        let editPcnCmd = <IOmniaCommand>{
            text: "Edit PCN analysis",
            icon: "fas fa-pen",
            foreground: true,
            canExecute: (r: RowViewModel) => r.hasAnalysis(),
            execute: (r: RowViewModel) => this.editAnalysis(r.analysis)
        }

        this._pcnAllocCommands = new components.OmniaCommandGroup(
            "Field",
            null,
            [allocPcnCmd, editPcnCmd]
        )

        this._pcnCommands = new components.OmniaCommandGroup(
            "PCN analysis",
            null,
            [editPcnCmd]
        )

    }

    private canGenerateSoilAnalysis(row: RowViewModel): boolean {

        if (row.soilAnalysis && row.soilAnalysis.CalculatedAverage) {
            return false
        }

        return !!row.soilAnalysisLayerId
    }

    private manageFieldAllocations(field: FieldViewModel) {

        this.shell.showComponentAsDialog("omnia-soil-soilanalysis-alloc-popup", field, {
            backdrop: "static",
            size: "xlarge"
        }).then(r => {
            this.refresh(0)
        })

    }

    private manageFieldPcnAllocations(field: FieldViewModel) {

        this.shell.showComponentAsDialog("omnia-soil-pcn-alloc-popup", field, {
            backdrop: "static",
            size: "xlarge"
        }).then(r => {
            this.refresh(0)
        })

    }

    private manageFieldHsaAllocations(field: FieldViewModel) {

        this.shell.showComponentAsDialog("omnia-soil-assessment-alloc-popup", field, {
            backdrop: "static",
            size: "xlarge"
        }).then(r => {
            this.svc.refresh().then(r => this.refresh(0))
        })

    }

    private generateAverageSoilAnalysis(row: RowViewModel) {
        if (row.soilAnalysisLayerId) {
            shell.showYesNo(this.shell, "You are about to generate an average soil analysis based on the analysis of its zones, for the field " + row.fieldDescription + ".<br/>Are you sure to generate this soil analysis ?",
                "Soil analysis generation").then(r => {
                    if (r) {
                        this.svc.generateSoilAnalysis([
                            {
                                fieldId: row.fieldId,
                                datalayerId: row.soilAnalysisLayerId
                            }])
                    }
                })
        }
    }

    canGenerateAverageSoilAnalyses() {
        return !this.rows || this.rows.filter(r => !!r.soilAnalysisLayerId).length === 0
    }

    generateAverageSoilAnalyses() {
        var scope = this.$scope.$new(true)
        scope["fields"] = this.rows.filter(r => !!r.soilAnalysisLayerId)

        this.$uibModal.open({
            template: "<omnia-soil-generate-popup />",
            scope: scope
        })
    }

    orderSoilAnalyses() {

        if (!this.session.currentUser.PhoneNumber) {

            shell.showWarning(this.shell,
                "Before ordering analysis, you must set your phone number. You can set it in \"Manage Account\" in the top right corner.",
                "Error")

            return

        }

        this.shell.showComponentAsDialog("omnia-soil-order-popup", { pcn: this.currentView && this.currentView.pcn }, { size: "lg" })

    }

    showColumnsPopup(): void {

        const scope = this.$scope.$new(true)

        const config = this.currentSubView?.getColumnsPopupConfig(this.columns)

        if (config) {

            scope["columns"] = config.columns
            scope["analysisColumns"] = config.analysisColumns

        }
        else {

            scope["columns"] = this.columns.columns

            if (this.soilView) {
                scope["analysisColumns"] = this.columns.standardAnalysisColumns
            }

            if (this.hsaView) {
                scope["columns"] = []
                scope["analysisColumns"] = this.columns.hsaColumns
            }

        }

        this.$uibModal.open({
            template: "<omnia-soil-columns-popup />",
            scope: scope
        })
    }


    toggleRow(row: RowViewModel) {

        this.toggleHistory(row)

    }

    private executeEditorCommand(analysis: IGenericAnalysis) {

        const { session, currentSubView: view } = this

        let cmd: any

        if (this.hsaView) {
            cmd = analysis ? analysis : {
                HealthySoilsAssessmentId: 0,
                SamplingDate: new Date(),
                FirstYearId: session.yearId,
                HoldingId: session.holdingId,
                Allocations: []
            }
        } else {
            cmd = {
                analysis: analysis,
                edit: true,
                context: this,
                alloc: null
            }
        }


        let cmp = this.currentSubView.editComponent + "-popup"
        let options: IOmniaShellDialogOptions = {
            size: view.editPopupSize,
            backdrop: "static",
            windowClass: view.editPopupClass
        }
        let r = this.shell.showComponentAsDialog(cmp, cmd, options)

        if (r) {

            r.then(ok => {
                if (ok) {
                    if (this.hsaView || this.pcnView) {
                        this.svc.refresh().then(r => this.refresh(0))
                    } else {
                        this.refresh(0)
                    }
                }
            })
        }

    }


    toggleHistory(row: RowViewModel) {
        this.togglePropery(row, "showHistory")
    }


    toggleAllocations(row: RowViewModel) {
        this.togglePropery(row, "showAllocations")
    }

    togglePropery(row: RowViewModel, property: string) {

        if (this.rows) {

            this.rows.forEach(r => {
                r[property] = (row == r ? !r[property] : false)
            })

        }
    }

    getNitrogenAnalysisTypeName(row: RowViewModel) {

        var na = row.nitrogenAnalysis

        if (na && na.AnalysisType) {
            return labels.NitrogenAnalysisType(na.AnalysisType)
        }

    }

    showAnalysisColumn(column: IAnalysisValueColumn) {

        var show = true

        if (column.alloc && (!this.fieldsView)) {
            show = false
        }

        return show

    }

    private createNewAnalysis() {
        this.executeEditorCommand(null)
    }

    private editAnalysis(analysis: IGenericAnalysis) {
        if (analysis) {
            this.executeEditorCommand(analysis)
        }
    }

    private handleRowCommandResult(row: RowViewModel, result: ng.IPromise<any>) {

        if (result) {
            result.then(r => {
                if (r) {
                    this.refresh(0, row.targetId)
                }
            })
        }

    }

    getHsaValue(row: RowViewModel, column: IAnalysisValueColumn): any {

        var assessment = <IHealthySoilsAssessment>row.analysis

        if (column.name === "InfiltrationRate") {
            if (!assessment.InfiltrationRate) {
                return null
            }

            return getInfiltrationScore(assessment.InfiltrationRate)
        }

        if (column.name === "Capping") {
            return assessment.Capping ? "Yes" : "No"
        }

        if (column.name === "Drainage") {
            if (!assessment.DrainageId) return null
            return this.hsaDrainages.find(d => d.HsaDrainageId === assessment.DrainageId).Name
        }

        if (column.name === "PrimaryCultivations") {
            if (!assessment.PrimaryCultivationsId) return null
            return this.primaryCultivations.find(d => d.HsaPrimaryCultivationsId === assessment.PrimaryCultivationsId).Name
        }

        if (column.name === "DrillType") {
            if (!assessment.DrillTypeId) return null
            return this.drillTypes.find(d => d.HsaDrillTypeId === assessment.DrillTypeId).Name
        }

        return assessment[column.name]

    }

    hasHsaValue(row: RowViewModel, column: IAnalysisValueColumn): boolean {
        return !!this.getHsaValue(row, column)
    }

    getGlobalFieldFilter() {
        return getGlobalFieldFilter(this.svc.fieldSvc)
    }
}

function getGlobalFieldFilter(svc: IFieldsService) {

    const filter = svc.filter
    const filtered = filter && (!filter.isEmpty())

    return filtered
        ? (row: RowViewModel) => isArrayEmpty(row.allocations) ? true : row.allocations.some(a => a.Field && a.Field.visible)
        : (row: RowViewModel) => true

}

function createViews(svc: ISoilService) {

    return [
        new SoilViews(),
        new NitrogenViews(),
        new HealthySoilsAssessmentViews(),
        new PcnViews(),
        SsmViews.create(svc)
    ]

}
